import { OrderQueryScopeEnum } from 'src/app/core/enums/order-query-scope.enum';
import { FacilityService } from './../../../../core/services/facility.service';
import { LaasOption } from 'src/app/core/models/laas-option';
import { SessionBaseComponent } from './../../../../shared/components/session.base.component';
import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { addDays, getDataScopeByPermission, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { FacilityOptions } from 'src/app/core/models/options/facility-options';
import { PurchaseSearchParams } from 'src/app/core/models/transport-operation/purchase-search-params';
import { copyFormControl } from 'src/app/core/utils/validation-functions';
import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { AdvisingServiceService } from '../../services/advising-service.service';
import { PurchaseSearchResult } from 'src/app/core/models/transport-operation/purchase-search-result';
import { FileCategory } from 'src/app/core/constants/file-category.constant';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { CodeConverterOptions } from 'src/app/core/models/options/code-converter-options';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { ForwarderService } from '../../services/forwarder.service';
import { endOfDay, startOfDay } from 'date-fns';
import { FileManagementComponent } from '../../components/file-management/file-management.component';
import { finalize } from 'rxjs/operators';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { PageInfo } from 'src/app/core/models/page-info';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';

@Component({
  selector: 'laas-purchase-search',
  templateUrl: './purchase-search.component.html',
  styleUrls: ['./purchase-search.component.scss'],
})
export class PurchaseSearchComponent extends SessionBaseComponent implements OnInit {

  readonly permissionCode: PermissionConstants = PermissionConstants.ViewQueryADStatus;
  currentLang: string = this.laasSetting.language;
  readonly preferenceKey: string = UserPreferenceConstants.PurchaseSearchResult;
  readonly fileCategory: FileCategory = FileCategory.Advising;
  readonly fileCategoryHawbNo: FileCategory = FileCategory.HawbNo;
  readonly dayRange: number = 21;
  readonly defaultCreateDt: Date[] = [
    startOfDay(addDays(new Date(), this.dayRange * -1)),
    endOfDay(new Date())
  ];
  protected readonly OrderQueryScopeEnum = OrderQueryScopeEnum;

  facilityOptions: Array<LaasOption> = [];
  forwarderOptions: Array<LaasOption> = [];
  statusOptions: Array<LaasOption> = [];
  transportPriorityOptions: LaasOption[] = [];
  vendorNumberOptions: LaasOption[] = [];
  priorityOptions: LaasOption[] = [];
  pcsrAndCsrOptions: LaasOption[] = [];

  isShowFileManagement: boolean = false;
  isSearchFormOpen: boolean = true;
  isLoading: boolean = false;

  searchForm: FormGroup;
  searchParams: PurchaseSearchParams = new PurchaseSearchParams();

  dataTableColumns: DateTableColumn[] = [];
  whProgressColumns : DateTableColumn[] = [];
  selectedRows: PurchaseSearchResult[] = [];
  results: PurchaseSearchResult[] = [];
  whResults: any[]=[];
  pageInfo:PageInfo = {
    pageIndex: 0,
    pageSize: 5,
    totalRecords: 0,
    totalPages: 0
  };

  sortOrder : SortOrder= {
    property: "rownum",
    direction: -1
  };

  @ViewChild("table") table!: DataTableComponent;
  @ViewChild('createDt') createDt!: CalendarComponent;
  @ViewChild('fileManagement') fileManagement!: FileManagementComponent;
  @ViewChild('fileManagementHawbNo') fileManagementHawbNo!: FileManagementComponent;

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    private dropdownOptionService: DropdownOptionsService,
    private facilityService: FacilityService,
    private advisingServiceService: AdvisingServiceService,
    private commonDataService: CommonDataService,
    private forwarderService: ForwarderService
  ) {
    super(injector);

    this.dataTableColumns = [
      { field: 'facility', header: 'Purchase.MainTable.Facility', sort: true },
      { field: 'adNo', header: 'Purchase.MainTable.AdNo', sort: true },
      { field: 'billOfLading', header: 'Purchase.MainTable.BillOfLading', sort: true },
      { field: 'salesName', header: 'Purchase.MainTable.SalesName', sort: true },
      { field: 'pcsrName', header: 'Purchase.MainTable.PcsrName', sort: true },
      { field: 'createDt', header: 'Purchase.MainTable.CreateDt', sort: true },
      { field: 'priority', header: 'Purchase.MainTable.Priority', sort: true },
      { field: 'vendor', header: 'Purchase.MainTable.Vendor', sort: true },
      { field: 'docStatus', header: 'Purchase.MainTable.DocStatus', sort: true }
    ];

    this.searchForm = this.fb.group({
      vendorNumbers: [],
      facilitys: [],
      adNos: [],
      billOfLadings: [],
      forwarder: null,
      importDeclarationNos: [],
      priorities: [],
      invoiceNos: [],
      docStatus: [],
      createDtStart: this.defaultCreateDt[0],
      createDtEnd: this.defaultCreateDt[1],
      orderQueryScope: [this.OrderQueryScopeEnum.all, Validators.required],
      pcsrAndCsrList: [],
    });


  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // rbac to maintain this page
    let notIncludes = !this.permissionCodes.includes(this.permissionCode);
    if (notIncludes) {
      // go unAuthorize
      this.routeStateService.navigateTo('/Error/unAuthorize');
    }

    this.dropdownOptionService.getFacilityOptions().subscribe({
      next: (options: FacilityOptions[]) => {
        this.facilityOptions = options.map((x) => ({
          label: x.optionDesc,
          value: x.optionCode,
        }));
      },
    });

    this.forwarderService.getForwarderOptions().subscribe({
      next: (options: any[]) => {
        this.forwarderOptions = options.map((x) => ({
          label: `${x.text}`.replace(' ', ' - '),
          value: x.value,
        }));

        this.translateService.get('Common.Message.ChooseOption').subscribe({
          next: (msg) => {
            this.forwarderOptions.unshift({
              label: msg,
              value: '',
            });
          },
        });
      },
    });

    this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList', 'advising')
      .subscribe({
        next: (results: CodeConverterOptions[]) => {
          this.statusOptions = results.map((x) => ({
            label: x.codeDesc,
            value: x.codeKey,
          }));

          this.translateService.get('Common.Message.ChooseOption').subscribe({
            next: (msg) => {
              this.statusOptions.unshift({
                label: msg,
                value: '',
              });
            },
          });
        },
      });

    this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList', 'TransportPriority').subscribe({
      next: result => {
        this.transportPriorityOptions = [...result].map<LaasOption>((x: CodeConverterOptions) => {
          return {
            label: `${x.codeKey} - ${x.codeDesc}`,
            value: x.codeKey,
          };
        });
      }
    });
    let urgentPriority = await this.translateService.get('Purchase.Option.Priority.Urgent').toPromise();
    let normalPriority = await this.translateService.get('Purchase.Option.Priority.Normal').toPromise();

    this.priorityOptions = [
      {
        value: '1',
        label: `1- ${urgentPriority}`,
      },
      {
        value: '5',
        label: `5 - ${normalPriority}`,
      },
    ];


    this.facilityService.getAllVendorOptions().subscribe({
      next: (options) => {
        for (let option of options) {
          this.vendorNumberOptions.push({
            label: `${option.optionCode} - ${option.optionDesc}`,
            value: option.optionCode,
          });
        }
      },
    });

    this.dropdownOptionService.getPcsrAndCsrOptions().subscribe({
      next: (options: any[]) => {
        this.pcsrAndCsrOptions = options.map((x) => ({
          label: x.optionDesc,
          value: x.optionCode,
        }));
      },
    });
  }

  onChipsAdd(event: any, formControlName: string): void {
    const value: string = event.value;
    const parsedValues: string[] = value.split('\r\n').map(x => x.trim()).filter((x) => isNullOrEmpty(x) === false && x !== '\r\n');

    let doNos: string[] = this.searchForm.controls[formControlName].value;
    doNos.splice(doNos.indexOf(value));
    doNos = doNos.concat(parsedValues);

    this.searchForm.controls[formControlName].setValue(doNos);
  }

  searchClick(event: Event): void {
    this.pageInfo.pageIndex = 0;
    this.table.setCurrentPage(0);
    this.getProgressResult();
  }

  getProgressResult(): void {
    this.searchParams = copyFormControl(this.searchForm, this.searchParams);
    this.searchParams.dataScopes = getDataScopeByPermission(this.userProfile, this.permissionCode);
    this.searchParams.userType = this.userProfile.userType;
    this.searchParams.userEmail = this.userService.userInfo.email;
    this.searchParams.pageIndex = this.pageInfo.pageIndex;
    this.searchParams.pageSize = this.pageInfo.pageSize;
    this.searchParams.sortOrder = this.sortOrder;
    this.searchParams.orderQueryScope = this.searchForm.value.orderQueryScope;
    this.table.setCurrentPage(0);
    this.isLoading = true;

    this.advisingServiceService.queryProgress(this.searchParams)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (results) => {
          console.log(results);
          this.results = results.content.map((x: any) => {
            x.vendor = (x.vendorNumber == null ? '' : x.vendorNumber) + '-' + (x.vendorName == null ? '' : x.vendorName);
            return x;
          });
          this.pageInfo.totalRecords = results.totalElements;
          this.table.setPageInfo(this.pageInfo.totalRecords);
          this.isLoading = false;
        },
        error:(err:any) =>{
          this.pageInfo.totalRecords = 0;
          this.table.setPageInfo(this.pageInfo.totalRecords);
          this.toastErrorMessage(err.error);
          this.isLoading = false;
        }
      });
  };

  openFileManagement(event: Event): void {
    this.isShowFileManagement = true;
  }

  onShowFileManagement(event: Event): void {
    this.fileManagement.resetComponent();
    this.fileManagementHawbNo.resetComponent();
  }

  getReferenceKeys(): string[] {
    return this.selectedRows.map((x) => {
      return x.adNo ?? '';
    }).filter(Boolean);
  }

  getReferenceKeysOfHawbNo(): string[] {
    return this.selectedRows.map((x) => {
      return x.billOfLading ?? '';
    }).filter(Boolean)
    .distinct();
  }

  gotoProgress(event: Event, adNo: string, facility: string, hawb:string ,flag:string, whNo:string): void {
    this.routeStateService.navigateTo(
      '/TransportOperation/InboundProgress',
      { adNo: adNo, facility: facility , hawb: hawb ,flag: flag, whNo: whNo},
      true
    );
  }

  resetBtnClick(event: Event): void {

    this.createDt.ngOnInit();

    this.searchForm.reset({
      vendorNumbers: [],
      facilitys: [],
      adNos: [],
      billOfLadings: [],
      forwarder: null,
      importDeclarationNos: [],
      priorities: [],
      invoiceNos: [],
      docStatus: [],
      createDtStart: this.defaultCreateDt[0],
      createDtEnd: this.defaultCreateDt[1],
      orderQueryScope: this.OrderQueryScopeEnum.all,
    });
  }

  dateOnChange(date: any, type: string) {
    if (type === 'createDt') {
      this.searchForm.controls.createDtStart.setValue(date.startDate);
      this.searchForm.controls.createDtEnd.setValue(date.endDate);
    }
  }

  tableSort(event: any): void {
    this.sortOrder = { property: event.field, direction: event.order };
    this.table.setCurrentPage(0);
    this.getProgressResult();
  }

  tablePageChange(event: any) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.getProgressResult();
  }
}
